// File: frontend/src/components/messages/NewMessageModal.tsx
import React, { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { quickSearch } from "../../services/searchService";
import { getConversationWith } from "../../services/messageService";
import { SEARCH_DEBOUNCE_MS, SEARCH_MIN_CHARS } from "../../constants/settings.constants";
import { SearchAuthorResult } from "../../models/SearchModel";
import { FaTimes } from "react-icons/fa";

interface PendingRecipient {
  userId: number;
  username: string;
  role: string;
}

interface NewMessageModalProps {
  onClose: () => void;
  onConversationStarted: (conversationId: number, recipientInfo?: PendingRecipient) => void;
}

/**
 * Modal for starting a new conversation.
 * User searches for a recipient by username, selects them,
 * and either opens an existing conversation or creates a new one.
 */
export const NewMessageModal: React.FC<NewMessageModalProps> = ({
  onClose,
  onConversationStarted,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState<SearchAuthorResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Search users with debounce using the existing quick search endpoint
  const searchUsers = useCallback(async (query: string) => {
    if (query.length < SEARCH_MIN_CHARS) {
      setResults([]);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use the existing quick search endpoint — returns grouped results per category
      const searchResult = await quickSearch(query);

      // Extract the authors category
      setResults(searchResult.authors || []);
    } catch (err: any) {
      console.error("User search failed:", err);
      setResults([]);
      // Show the actual backend error message for easier debugging
      setError(err.message || t("messages_search_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Clear-X inside the user search box — resets the query and the result list
  const handleClearSearch = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setSearchQuery("");
    setResults([]);
    setError(null);
    inputRef.current?.focus();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Debounce search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchUsers(query);
    }, SEARCH_DEBOUNCE_MS);
  };

  // Handle selecting a user
  const handleSelectUser = async (selectedUser: SearchAuthorResult) => {
    try {
      setError(null);

      // Check if conversation already exists
      const { conversationId } = await getConversationWith(selectedUser.userId);

      const recipientInfo: PendingRecipient = {
        userId: selectedUser.userId,
        username: selectedUser.username,
        role: selectedUser.role,
      };

      if (conversationId > 0) {
        // Existing conversation — open it
        onConversationStarted(conversationId, recipientInfo);
      } else {
        // No existing conversation — pass recipient info so parent can handle it
        onConversationStarted(-1, recipientInfo);
      }
    } catch (err: any) {
      setError(err.message || t("messages_start_error"));
    }
  };

  // Close on overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Whether we should show the "no results" message:
  // only when there is NO error, search is done, results are empty, and query is long enough
  const showNoResults =
    !loading &&
    !error &&
    results.length === 0 &&
    searchQuery.length >= SEARCH_MIN_CHARS;

  return (
    <div className="new-message-overlay" onClick={handleOverlayClick}>
      <div className="new-message-modal">
        <div className="new-message-modal-header">
          <h3>{t("messages_new")}</h3>
          <button className="new-message-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="new-message-search">
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={t("messages_search_user_placeholder")}
          />
          {searchQuery !== "" && (
            <button
              className="new-message-clear-btn"
              onClick={handleClearSearch}
              title={t("admin_clear_filters")}
            >
              <FaTimes />
            </button>
          )}
        </div>

        {/* Error shown alone — never together with "no results" */}
        {error && <div className="new-message-error">{error}</div>}

        <div className="new-message-results">
          {loading && (
            <div className="new-message-no-results">{t("loading")}</div>
          )}

          {/* "No users found" only when search succeeded but returned nothing */}
          {showNoResults && (
            <div className="new-message-no-results">
              {t("messages_no_users_found")}
            </div>
          )}

          {!loading &&
            results.map((user) => (
              <div
                key={user.userId}
                className="new-message-user-item"
                onClick={() => handleSelectUser(user)}
              >
                <div className="new-message-user-avatar">
                  {user.username.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="new-message-user-name">{user.username}</div>
                  <div className="new-message-user-role">
                    {t(`status_${user.role.toLowerCase()}`)}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};